import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { LAST_ACTIVITY_KEY } from '../constants';
import { forceLogoutUser } from '../helpers/LogoutHelper';

const AUTO_LOGOUT_DELAY_MS = 3 * 60 * 60 * 1000; // 3 hours

const useInactivityDetector = () => {
  const autoLogoutTimeoutRef = useRef(null);

  const { idToken } = useSelector((state) => state.User);

  // Debounced activity handler
  const handleUserOperation = useMemo(
    () =>
      _.debounce(() => {
        if (idToken) {
          localStorage.setItem(LAST_ACTIVITY_KEY, dayjs().valueOf().toString());

          if (autoLogoutTimeoutRef.current) {
            clearTimeout(autoLogoutTimeoutRef.current);
          }

          autoLogoutTimeoutRef.current = setTimeout(() => {
            forceLogoutUser(true);
          }, AUTO_LOGOUT_DELAY_MS);
        }
      }, 200),
    [idToken]
  );

  useEffect(() => {
    if (idToken) {
      const now = dayjs();
      const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
      const lastActivity = lastActivityStr
        ? dayjs(Number(lastActivityStr))
        : null;

      if (lastActivity && now.diff(lastActivity) >= AUTO_LOGOUT_DELAY_MS) {
        forceLogoutUser(true);
        return;
      }

      const remainingTime = lastActivity
        ? AUTO_LOGOUT_DELAY_MS - now.diff(lastActivity)
        : AUTO_LOGOUT_DELAY_MS;

      autoLogoutTimeoutRef.current = setTimeout(() => {
        forceLogoutUser(true);
      }, remainingTime);

      const events = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
      events.forEach((event) =>
        window.addEventListener(event, handleUserOperation)
      );

      return () => {
        events.forEach((event) =>
          window.removeEventListener(event, handleUserOperation)
        );
        if (autoLogoutTimeoutRef.current) {
          clearTimeout(autoLogoutTimeoutRef.current);
        }
        // cancel debounce on cleanup
        handleUserOperation.cancel();
      };
    }
  }, [idToken, handleUserOperation]);

  return;
};

export default useInactivityDetector;
